import { AxiosError } from 'axios'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import APIServices from '../../api/APIServices'

export interface CommercialPerson {
  id: number
  username: string
  displayName: string
  email: string
  isActive: boolean
  businessId: number
  avatar: string | null
  lastLogin: string
  roles: {
    name: string
    code: string
  }[]
}

// Create a unique, descriptive key for this query
const COMMERCIAL_PERSONNEL_QUERY_KEY = 'commercial-personnel'

type BusinessCommercialsResponse = CommercialPerson[]

/**
 * Fetch commercial personnel from the API
 * Separating API call logic from the hook for better testability
 */
const fetchBusinessCommercials =
  async (): Promise<BusinessCommercialsResponse> => {
    const response = await APIServices.get('/invoice/commercial/all')
    return response.data
  }

/**
 * Custom hook for fetching commercial personnel (comerciales)
 *
 * @param params - Query parameters including businessId
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useBusinessCommercialsQuery = ({
  businessId,
  options,
}: {
  businessId: number
  options?: Partial<UseQueryOptions<BusinessCommercialsResponse, AxiosError>>
}) => {
  // Query hook
  const queryResult = useQuery<BusinessCommercialsResponse, AxiosError>({
    // Create a structured query key that includes all parameters
    queryKey: [COMMERCIAL_PERSONNEL_QUERY_KEY, businessId],

    // Separate the API call logic from the query configuration
    queryFn: () => fetchBusinessCommercials(),

    // Default options that can be overridden
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Apply any custom options
    ...options,
  })

  return {
    businessCommercials: queryResult.data || [],
    isLoadingBusinessCommercials: queryResult.isLoading,
    isErrorBusinessCommercials: queryResult.isError,
    errorBusinessCommercials: queryResult.error,
    refetchBusinessCommercials: queryResult.refetch,
    isFetchingBusinessCommercials: queryResult.isFetching,
  }
}

/**
 * Helper function to get the delivery personnel query key
 * Useful for cache invalidation or prefetching
 */
export const getBusinessCommercialsQueryKey = (businessId: number) => [
  COMMERCIAL_PERSONNEL_QUERY_KEY,
  businessId,
]
